import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Fragment, useEffect, useState } from 'react';
import { checkout } from 'self-service-api/core/utilities/links';
import styled from 'styled-components';
import UIContactSalesButton from 'ui-addon-upgrades/button/UIContactSalesButton';
import { getUpgradeDataTrackingProperties } from 'ui-addon-upgrades/ums/utils';
import { CheckoutButtonPropsInterface } from 'ui-addon-upgrades/_core/checkout/CheckoutPropsInterface';
import { generatePurchaseOrder } from 'ui-addon-upgrades/_core/checkout/generatePurchaseOrder';
import { REQUEST_SKU } from 'ui-addon-upgrades/_core/common/constants/AdminModalTypes';
import { BILLING } from 'ui-addon-upgrades/_core/common/constants/AdminTypes';
import { upgradeDataPropsInterface } from 'ui-addon-upgrades/_core/common/data/upgradeData/interfaces/upgradeDataPropsInterface';
import { track, trackBeforeUnload } from 'ui-addon-upgrades/_core/common/eventTracking/tracker';
import { navigateTo } from 'ui-addon-upgrades/_core/common/navigation/url';
import { getIsQuantityUpgrade, getProductTrackingName } from 'ui-addon-upgrades/_core/utils/productUtils';
import UIFloatingAlertList from 'UIComponents/alert/UIFloatingAlertList';
import UIButton from 'UIComponents/button/UIButton';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UIButtonWrapper from 'UIComponents/layout/UIButtonWrapper';
import { omit } from '../../utils';
import { getProducts } from '../common/api/getProducts';
import AdminOnlyWrapper from '../common/components/AdminOnlyWrapper';
import logPurchaseOrderSentry from '../common/reliability/logCheckoutError';
import logError from '../common/reliability/logError';
import CheckoutFloatingAlertStore from './CheckoutFloatingAlertStore';
const StyledContactSalesButton = styled(UIContactSalesButton).withConfig({
  displayName: "CheckoutButton__StyledContactSalesButton",
  componentId: "sc-5tcrye-0"
})(["display:none !important;"]);

const CheckoutButton = props => {
  const {
    upgradeData,
    productConfigurations = [],
    offerId,
    currencyCode,
    productBasedOffers = [],
    additionalUnitQuantity,
    onClick = () => {},
    onError,
    children,
    beforeRedirect = () => Promise.resolve(true),
    products = null,
    shouldShowErrorAlert,
    isUpgradePoint
  } = props,
        rest = _objectWithoutPropertiesLoose(props, ["upgradeData", "productConfigurations", "offerId", "currencyCode", "productBasedOffers", "additionalUnitQuantity", "onClick", "onError", "children", "beforeRedirect", "products", "shouldShowErrorAlert", "isUpgradePoint"]);

  const [lastOrderGenerationFailed, setLastOrderGenerationFailed] = useState(false);
  const [isGeneratingPurchaseOrder, setIsGeneratingPurchaseOrder] = useState(false);
  const [productList, setProductList] = useState(products);
  const [hasError, setHasError] = useState(false);
  const skuIds = productConfigurations.map(({
    skuId
  }) => skuId);
  const [trackingProperties, setTrackingProperties] = useState({});

  const getCheckoutButtonInteractionTrackingProperties = () => {
    const productNames = skuIds.map(skuId => {
      return getProductTrackingName(productList, skuId);
    });
    const isQuantityUpgrade = getIsQuantityUpgrade(productList, productConfigurations);
    return {
      additionalUnitQuantity,
      currencyCode,
      productBasedOfferIds: productBasedOffers,
      isQuantityUpgrade,
      offerId,
      checkoutType: 'full',
      skuIds,
      productNames // TODO: Add quantity if quantity upgrade?

    };
  };

  useEffect(() => {
    if (productList) {
      setTrackingProperties(Object.assign({}, getUpgradeDataTrackingProperties(upgradeData, isUpgradePoint), {}, getCheckoutButtonInteractionTrackingProperties()));
      track('checkoutButtonInteraction', Object.assign({
        action: 'viewed'
      }, getUpgradeDataTrackingProperties(upgradeData, isUpgradePoint), {}, getCheckoutButtonInteractionTrackingProperties()));
    } else {
      getProducts().then(setProductList).catch(setProductList(null));
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [productList]);

  const onOrderGenerationStart = e => {
    setLastOrderGenerationFailed(false);
    setIsGeneratingPurchaseOrder(true);
    trackBeforeUnload('checkoutButtonInteraction', Object.assign({
      action: 'clicked'
    }, trackingProperties));

    if (onClick) {
      onClick(e);
    }
  };

  const onOrderGenerationComplete = () => {
    setLastOrderGenerationFailed(false);
    setIsGeneratingPurchaseOrder(false);
    trackBeforeUnload('checkoutButtonInteraction', Object.assign({
      action: 'purchase order generated'
    }, trackingProperties));
  };

  const onOrderGenerationFailed = err => {
    if (onError) {
      onError(err);
    }

    setLastOrderGenerationFailed(true);
    setIsGeneratingPurchaseOrder(false);
    track('checkoutButtonInteraction', Object.assign({
      action: 'purchase order generation failed'
    }, trackingProperties));
    logError('generatePurchaseOrder', err, Object.assign({}, trackingProperties));
    logPurchaseOrderSentry('generatePurchaseOrder', err, Object.assign({
      buttonName: 'CheckoutButton'
    }, trackingProperties));

    if (shouldShowErrorAlert) {
      setHasError(true);
      CheckoutFloatingAlertStore.addAlert({
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "upgrades.buyNowFailureAlert.title"
        }),
        message: /*#__PURE__*/_jsxs(UIButtonWrapper, {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "upgrades.buyNowFailureAlert.message"
          }), /*#__PURE__*/_jsx(UIButton, {
            onClick: () => {
              CheckoutFloatingAlertStore.resetStore();
              document.getElementById('hidden-contact-sales-button').click();
            },
            use: "tertiary",
            size: "extra-small",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "upgrades.contactSales"
            })
          })]
        }),
        type: 'danger'
      });
    }
  };

  const handleClick = e => {
    if (onClick) {
      onClick();
    }

    onOrderGenerationStart(e);
    generatePurchaseOrder(currencyCode, productConfigurations, productList, offerId, productBasedOffers).then(orderId => {
      onOrderGenerationComplete();
      const checkoutUrl = checkout(orderId, upgradeData && upgradeData.upgradeSource);
      beforeRedirect(checkoutUrl).then((shouldRedirect = true) => shouldRedirect && navigateTo(checkoutUrl)).catch(onOrderGenerationFailed);
    }).catch(onOrderGenerationFailed);
  };

  const propsToPass = omit(rest, ['allowModal', 'shouldShowErrorAlert', 'products', 'isUpgradePoint', 'level', 'trackingSource', 'canViewNewSeats']); //  This is a hidden button hack for when there is an error. Basically, we want to show an alert with a TTS button in it.
  //  However, when the user clicks that button, we want to remove the alert. Unfortunately, the modal with the TTS CTAS is
  //  mounted in the UIContactSalesButton. So, we don't want to actually remove the real button from the page, so we will render
  //  this hidden button and show a dummy button that simulates the click.

  return /*#__PURE__*/_jsxs(Fragment, {
    children: [hasError && shouldShowErrorAlert && /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(StyledContactSalesButton, {
        upgradeData: Object.assign({}, upgradeData, {
          uniqueId: 'checkout-error'
        }),
        id: "hidden-contact-sales-button"
      }), /*#__PURE__*/_jsx(UIFloatingAlertList, {
        alertStore: CheckoutFloatingAlertStore
      })]
    }), /*#__PURE__*/_jsx(AdminOnlyWrapper, {
      upgradeData: upgradeData,
      adminType: BILLING,
      modalType: REQUEST_SKU,
      children: /*#__PURE__*/_jsx(UILoadingButton, Object.assign({}, propsToPass, {
        failed: lastOrderGenerationFailed,
        loading: isGeneratingPurchaseOrder,
        onClick: handleClick,
        "data-selenium": "checkout-button",
        "data-reagan-primary-cta": true,
        children: children || /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'upgrades.checkoutButton.default'
        })
      }))
    })]
  });
};

CheckoutButton.defaultProps = {
  use: 'primary'
};
CheckoutButton.propTypes = Object.assign({}, upgradeDataPropsInterface, {}, omit(CheckoutButtonPropsInterface, 'offerProductConfigurations', 'nonRecurringProductConfigurations'));
export default CheckoutButton;